import React, { memo, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import * as dayjs from 'dayjs';

import { teamDetails, updateTeam, deleteTeam } from '../../services/teams';
import { removeFromTeam, removeInvite, invitesList } from '../../services/users';

import { RemoveUserDialog } from "../../components/removeUserDialog";
import Table from '../../components/Table';
import { PracticeCalendar } from '../../components/PracticeCalendar';
import { InviteMemberButton } from '../../components/InviteMember';
import Select from "react-select";
import ConfirmRemoveDialog from '../../components/ConfirmRemoveDialog';
import { ageStyles } from "../../components/shared/styles";
import ReturnButton from '../../components/ReturnButton';

const TeamDetails = () => {
  const [team, setTeam] = useState({});
  const [coaches, setCoaches] = useState([]);
  const [messages, setMessages] = useState([]);
  const [players, setPlayers] = useState([]);
  const [parents, setParents] = useState([]);
  const [invites, setInvites] = useState([]);

  const { t } = useTranslation();

  const [viewTeam, setViewTeam] = useLocalStorage('viewTeam', {});
  const [teamDialog, setTeamDialog] = useState(false);
  const [tab, setTab] = useState('practices');

  let { teamId } = useParams();

  useEffect(() => {
    `${viewTeam?.id}` === teamId && setTeam(viewTeam);
  }, []); // Set current team while loading data

  const getTeam = async () => {
    const { team, users } = await teamDetails(teamId);
    setTeam(team);
    setCoaches(users.filter(u => ['coach', 'assistant'].includes(u.role)));
    setPlayers(users.filter(u => u.role === 'player'));
    setParents(users.filter(u => u.role === 'viewer'));
  };

  const getInvites = async () => {
    const { invites } = await invitesList({ team_id: teamId });
    setInvites(invites);
  };

  useEffect(() => {
    getTeam();
    getInvites();
  }, []);

  return (
    <div className="team-details">
      <ReturnButton/>
      <div className="team-details-header">
        <div className="team-details-header-text">
          {team?.name}
          <i className="icon icon-settings edit-icon pointer" onClick={() => setTeamDialog(true)}></i>
        </div>
        { ['coach', 'player', 'viewer', 'invites'].includes(tab) &&
         <InviteMemberButton role={tab} team={team} onClose={(reload) => reload && getTeam()}/>
        }
      </div>
      <div className="team-details-tabs">
        <div
          className={`team-details-tabs-option pointer ${tab === 'practices' ? 'active' : ''}`}
          onClick={() => setTab('practices')}
        >{`${t('teams.practices')}`}</div>
        {/* <div
          className={`team-details-tabs-option pointer ${tab === 'messages' ? 'active' : ''}`}
          onClick={() => setTab('messages')}
        >{`${t('teams.messages', { count: messages.length })} `}</div> */}
        <div
          className={`team-details-tabs-option pointer ${tab === 'coach' ? 'active' : ''}`}
          onClick={() => setTab('coach')}
        >{`${t('teams.coaches', { count: coaches.length })}`}</div>
        <div
          className={`team-details-tabs-option pointer ${tab === 'player' ? 'active' : ''}`}
          onClick={() => setTab('player')}
        >{`${t('teams.players', { count: players.length })}`}</div>
        <div
          className={`team-details-tabs-option pointer ${tab === 'viewer' ? 'active' : ''}`}
          onClick={() => setTab('viewer')}
        >{`${t('teams.parents', { count: parents.length })} `}</div>
        <div
          className={`team-details-tabs-option pointer ${tab === 'invites' ? 'active' : ''}`}
          onClick={() => setTab('invites')}
        >{`${t('coaches.pending', { count: invites.length })} `}</div>
      </div>
      <div className="team-details-list">
        {tab === 'practices' && <PracticeCalendar/>}
        {tab === 'messages' && <TeamMessages messages={messages} />}
        {tab === 'coach' && <TeamCoaches data={coaches} reload={getTeam} />}
        {tab === 'player' && <TeamCoaches data={players} reload={getTeam} />}
        {tab === 'viewer' && <TeamCoaches data={parents} reload={getTeam} />}
        {tab === 'invites' && <InvitesTable invites={invites} reload={getInvites} />}
      </div>
      {teamDialog && <TeamDialog team={team} onClose={(team) => {team && setTeam(team); setTeamDialog(false)}}
      />}
    </div>
  );
};

export default memo(TeamDetails);

const TeamCoaches = ({ data, reload }) => {
  let { teamId } = useParams();
  let navigate = useNavigate();

  const { t, i18n } = useTranslation();

  const editCoach = coach => {
    navigate(`/club_admin/teams/${teamId}/${coach?.id}`);
  };

  const columns = React.useMemo(
    () => [
      {
        id: 'online',
        header: ' ',
        cell: info => (<OnlineStatus active={info.row.original?.active}/>),
        meta: { style: { width: 20 } },
      },
      {
        header: t('common.table_name'),
        accessorKey: 'name',
        cell: info => (
          <div onClick={() => editCoach(info.row.original)}>
            {info.getValue()}
            {info.row.original?.is_manager ? <span className="admin-badge">Admin</span> : ''}
          </div>
        ),
      },
      { header: t('common.table_email'), accessorKey: 'email' },
      // { header: t('teams.table_deadline'), accessorKey: 'remove_at' },
      {
        header: t('teams.table_last'),
        accessorKey: 'last_seen_at',
        cell: info => info.getValue() && dayjs(info.getValue()).format('YYYY-MM-DD'),
      },
      {
        id: 'actions',
        header: ' ',
        cell: info => (
          <div>
            <RemoveUserDialog user={info.row.original} teamId={teamId} onClose={(needReload) => needReload && reload()}><i className="icon icon-user-unfollow"></i></RemoveUserDialog>
            <i className="icon icon-settings" onClick={() => editCoach(info.row.original)}></i>
          </div>
        ),
        meta: { style: { width: 60 } },
      },
    ],
    [i18n.language]
  );

  return <Table {...{ data, columns }} />;
};

const TeamMessages = ({ messages }) => {
  return <div> Messages </div>;
};

const InvitesTable = ({ invites, reload }) => {
  const { t } = useTranslation();


  const removeInvitation = async (invite) => {
    await removeInvite(invite.id);
    reload();
  }

  const columns = React.useMemo(
    () => [
      { header: t('common.table_name'), accessorKey: 'name', cell: info => <div>
          {info.getValue()}
        </div> },
      { header: t('common.table_email'), accessorKey: 'email', cell: info => info.getValue() },
      { header: t('coaches.table_role'), accessorKey: 'role', cell: info => info.getValue() },
      { header: t('coaches.table_invite'), accessorKey: 'created_at', cell: info => dayjs(info.getValue()).format('YYYY-MM-DD'), },
      {
        id: 'actions',
        header: " ", cell: (info) =>
          <div>
            <i className="icon icon-trash" onClick={() => confirm(t('coaches.confirm_remove_invite', { name:info.row.original?.email})) && removeInvitation(info.row.original)}></i>
          </div>,  meta: { style: { width: 60 }}
      },
    ],
    []
  )

  return(
    <Table {...{ data: invites, columns }}/>
  )
}


const OnlineStatus = (active) => {
  return <div className={`status-indicator status-indicator-${ active === true ? 'active' : 'passive' }`}>&bull;</div>
}

const TeamDialog = ({ team, onClose }) => {
  const ages = [5,6,7,8,9,10,11,12,13,14,15];
  const [form, setForm] = useState({
    name: team?.name || '',
    age: team?.age || 10,
    minutes_per_practice: team?.minutes_per_practice || 90,
    difficulty_level: team?.difficulty_level || 'intermediate',
    individuals_age: team?.individuals_age || 10,
    minutes_per_individual_practice: team?.minutes_per_individual_practice || 75
  })
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { t } = useTranslation();
  let navigate = useNavigate();

  const save = async () => {
    if (!form.name.trim()) return;

    try {
      const result = await updateTeam({...team, ...form});
      close(result.team);
    } catch (error) {
      console.error('Error saving team:', error);
    }
  };

  const close = (changed) => {
    onClose && onClose(changed)
  }

  const confirmRemove = async () => {
    await deleteTeam(team.id);
    close();
    setShowConfirmDialog(false);
    navigate(`/club_admin/teams`);
  };

  return (
    <div className="dialog">
      <div className="dialog-content">
        <div className="close" onClick={() => close(false)}></div>
        <div className="form-group">
          <label htmlFor="groupName">{t('common.table_name')}</label>
          <input
            id="name"
            type="text"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
          />
        </div>

        <div className="dialog-content-2-columns">
          <div>
            <h2>{t('teams.practices_for_team')}</h2>
            <div className='form-container-area'>
              <div className="form-group">
                <label htmlFor="groupName">{t('teams.age')}</label>
                <Select
                  styles={ageStyles}
                  closeMenuOnSelect={true}
                  options={ages.map(value => ({ value, label: value}))}
                  isSearchable={false}
                  onChange={({ value }) => setForm({...form, age: value })}
                  value={{ value: form.age, label: form.age }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="groupName">{t('teams.difficulty.level')}</label>
                <Select
                  styles={ageStyles}
                  closeMenuOnSelect={true}
                  options={['easy', 'intermediate', 'hard', 'academy'].map(value => ({ value, label: t(`teams.difficulty.${value}`)}))}
                  isSearchable={false}
                  onChange={({ value }) => setForm({...form, difficulty_level: value })}
                  value={{ value: form.difficulty_level, label: t(`teams.difficulty.${form.difficulty_level}`) }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="groupName">{t('teams.minutes_per_practice')}</label>
                <input
                  id="name"
                  type="text"
                  value={form.minutes_per_practice}
                  onChange={(e) => setForm({...form, minutes_per_practice: e.target.value})}
                />
              </div>
            </div>
          </div>

          <div>
            <h2>{t('teams.practices_for_individuals')}</h2>
              <div className='form-container-area'>
                <div className="form-group">
                  <label htmlFor="groupName">{t('teams.age')}</label>
                  <Select
                    styles={ageStyles}
                    closeMenuOnSelect={true}
                    options={ages.map(value => ({ value, label: value}))}
                    isSearchable={false}
                    onChange={({ value }) => setForm({...form, individuals_age: value })}
                    value={{ value: form.individuals_age, label: form.individuals_age }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="groupName">{t('teams.minutes_per_practice')}</label>
                  <input
                    id="name"
                    type="text"
                    value={form.minutes_per_individual_practice}
                    onChange={(e) => setForm({...form, minutes_per_individual_practice: e.target.value})}
                  />
                </div>
              </div>
          </div>
        </div>

        <div className="form-container-actions">
          {team?.id && (
            <button className="button button-dialog-delete" onClick={() => setShowConfirmDialog(true)}>{t('buttons.common.delete')}</button>
          )}
          <div className="flex-end">
            <button className="button" onClick={() => close(false)}>{t('buttons.common.cancel')}</button>
            <button className="button button-primary" onClick={save}>{t('buttons.common.save')}</button>
          </div>
        </div>
      </div>
      {showConfirmDialog && (
        <ConfirmRemoveDialog
          title={t('teams.remove_team', { title: form.name })}
          confirm={confirmRemove}
          close={() => setShowConfirmDialog(false)}
        />
      )}
    </div>
  );
};
